import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Droplets, Eye, EyeOff, Mail, Lock, User, Phone, Briefcase, Building2, FileText } from 'lucide-react';

const AuthPage = () => {
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [clientType, setClientType] = useState('individual'); // individual or corporate
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ 
    name: '', email: '', password: '', phone: '', company_name: '', nif: ''
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success('Bem-vindo de volta!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao entrar');
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Validation for corporate clients
    if (clientType === 'corporate') {
      if (!registerData.company_name) {
        toast.error('Nome da empresa é obrigatório para clientes corporate');
        return;
      }
      if (!registerData.nif) {
        toast.error('NIF é obrigatório para clientes corporate');
        return;
      }
    }
    
    setLoading(true);
    try {
      await register(
        registerData.name,
        registerData.email,
        registerData.password,
        registerData.phone,
        'customer',
        clientType,
        registerData.company_name,
        registerData.nif
      );
      toast.success('Conta criada com sucesso!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar conta');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-zinc-100 flex flex-col">
      {/* Header with gradient */}
      <div className="bg-gradient-to-br from-sonangol-red to-sonangol-orange pt-8 pb-20 px-6 rounded-b-[2rem]">
        <div className="text-center">
          {/* Sonangol Logo */}
          <div className="mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_d7626c81-a248-46cd-9b36-40ecfdffbad5/artifacts/2ubuie86_logo_sonangol_undp.png"
              alt="Sonangol Distribuição e Comercialização"
              className="h-16 mx-auto object-contain"
              data-testid="sonangol-logo"
            />
          </div>
          <div className="w-14 h-14 mx-auto bg-white rounded-xl flex items-center justify-center shadow-lg mb-3">
            <Droplets className="w-8 h-8 text-sonangol-red" />
          </div>
          <h1 className="font-barlow text-3xl font-bold text-white uppercase tracking-tight">
            HáLubri
          </h1>
          <p className="text-white/80 text-sm mt-1 font-manrope">Lubrificantes Sonangol</p>
        </div>
      </div>

      {/* Auth form */}
      <div className="flex-1 px-4 -mt-10">
        <div className="bg-white rounded-2xl shadow-xl p-6 max-w-md mx-auto">
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="login-tab">Entrar</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Criar Conta</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="seu@email.com"
                      className="pl-10 h-12"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="login-password">Palavra-passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                    <Input
                      id="login-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      className="pl-10 pr-10 h-12"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 bg-sonangol-red hover:bg-red-700 font-barlow font-bold uppercase tracking-wider"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? 'A entrar...' : 'Entrar'}
                </Button>
                
                <div className="text-center">
                  <Link 
                    to="/forgot-password" 
                    className="inline-block py-2 text-sonangol-red hover:underline text-sm font-medium"
                    data-testid="forgot-password-link"
                  >
                    Esqueci a palavra-passe
                  </Link>
                </div>
              </form>
            </TabsContent>
            
            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                {/* Client Type Selection */}
                <div className="space-y-2">
                  <Label>Tipo de Cliente</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setClientType('individual')}
                      className={`p-3 rounded-xl border-2 transition-all ${
                        clientType === 'individual' 
                          ? 'border-sonangol-red bg-sonangol-red/5' 
                          : 'border-zinc-200 hover:border-zinc-300'
                      }`}
                      data-testid="client-type-individual"
                    >
                      <User className={`w-6 h-6 mx-auto mb-1 ${clientType === 'individual' ? 'text-sonangol-red' : 'text-zinc-400'}`} />
                      <p className={`text-sm font-medium ${clientType === 'individual' ? 'text-sonangol-red' : 'text-zinc-600'}`}>Particular</p>
                      <p className="text-xs text-zinc-400">Compras individuais</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setClientType('corporate')}
                      className={`p-3 rounded-xl border-2 transition-all ${
                        clientType === 'corporate' 
                          ? 'border-sonangol-orange bg-sonangol-orange/5' 
                          : 'border-zinc-200 hover:border-zinc-300'
                      }`}
                      data-testid="client-type-corporate"
                    >
                      <Building2 className={`w-6 h-6 mx-auto mb-1 ${clientType === 'corporate' ? 'text-sonangol-orange' : 'text-zinc-400'}`} />
                      <p className={`text-sm font-medium ${clientType === 'corporate' ? 'text-sonangol-orange' : 'text-zinc-600'}`}>Empresa</p>
                      <p className="text-xs text-zinc-400">Compras em volume</p>
                    </button>
                  </div>
                </div>

                {/* Corporate Info (conditional) */}
                {clientType === 'corporate' && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="register-company">Nome da Empresa *</Label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                        <Input
                          id="register-company"
                          type="text"
                          placeholder="Empresa XPTO, Lda"
                          className="pl-10 h-12"
                          value={registerData.company_name}
                          onChange={(e) => setRegisterData({ ...registerData, company_name: e.target.value })}
                          required
                          data-testid="register-company-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-nif">NIF (Número de Identificação Fiscal) *</Label>
                      <div className="relative">
                        <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                        <Input
                          id="register-nif"
                          type="text"
                          placeholder="5000123456"
                          className="pl-10 h-12"
                          value={registerData.nif}
                          onChange={(e) => setRegisterData({ ...registerData, nif: e.target.value })}
                          required
                          data-testid="register-nif-input"
                        />
                      </div>
                    </div>
                    <div className="bg-orange-50 border border-orange-200 rounded-xl p-3">
                      <p className="text-orange-800 text-xs">
                        <strong>Nota:</strong> Clientes corporate têm acesso a compras em grande volume com entrega exclusiva ao endereço definido.
                      </p>
                    </div>
                  </>
                )}

                <div className="space-y-2">
                  <Label htmlFor="register-name">{clientType === 'corporate' ? 'Nome do Responsável' : 'Nome Completo'}</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                    <Input
                      id="register-name"
                      type="text"
                      placeholder="João Silva"
                      className="pl-10 h-12"
                      value={registerData.name}
                      onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                      required
                      data-testid="register-name-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="register-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                    <Input
                      id="register-email"
                      type="email"
                      placeholder="seu@email.com"
                      className="pl-10 h-12"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      required
                      data-testid="register-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="register-phone">Telefone</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                    <Input
                      id="register-phone"
                      type="tel"
                      placeholder="+244 923 456 789"
                      className="pl-10 h-12"
                      value={registerData.phone}
                      onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                      data-testid="register-phone-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="register-password">Palavra-passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                    <Input
                      id="register-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      className="pl-10 pr-10 h-12"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      required
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 bg-sonangol-red hover:bg-red-700 font-barlow font-bold uppercase tracking-wider"
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? 'A criar...' : 'Criar Conta'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          {/* Funcionario Link */}
          <div className="mt-6 pt-6 border-t border-zinc-100 space-y-4">
            <Link to="/funcionario" data-testid="funcionario-link">
              <div className="flex items-center justify-between p-4 bg-zinc-800 rounded-xl hover:bg-zinc-700 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-sonangol-orange rounded-lg flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-barlow font-semibold">Portal Funcionário</p>
                    <p className="text-zinc-400 text-xs">Gestão de pedidos da estação</p>
                  </div>
                </div>
                <span className="text-sonangol-orange text-sm">Entrar →</span>
              </div>
            </Link>
            
            <Link to="/delivery" data-testid="delivery-link" className="block mt-2">
              <div className="flex items-center justify-between p-4 bg-green-600/10 border border-green-600/30 rounded-xl hover:bg-green-600/20 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-green-600 font-barlow font-semibold">Portal Entregador</p>
                    <p className="text-zinc-500 text-xs">Parceiros de entrega</p>
                  </div>
                </div>
                <span className="text-green-600 text-sm">Entrar →</span>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="py-6 text-center text-zinc-400 text-xs font-manrope">
        © 2025 Sonangol E.P. · Todos os direitos reservados
      </div>
    </div>
  );
};

export default AuthPage;
